import { API_BASE } from "@/config/api";
import { fetchWithAuth } from "@/lib/api";

export type AdminAssetType = "stock" | "etf" | "crypto" | "fund";

export interface AdminFiatOption {
  asset_id: string;
  symbol: string;
  name: string;
}

export interface AdminAssetFormOptions {
  fiat_currencies: AdminFiatOption[];
  exchanges: string[];
}

export interface AdminAssetPayload {
  type: AdminAssetType;
  name: string;
  symbol: string;
  exchange?: string;
  currency?: string;
  isin?: string;
  platform?: string;
  contract_addr?: string;
  sync_after_create: boolean;
}

export interface AdminCreatedAsset {
  asset_id: string;
  name: string;
  symbol: string;
  type: AdminAssetType;
  exchange?: string;
  currency?: string;
  isin?: string;
  platform?: string;
  contract_addr?: string;
  sync: boolean;
  last_sync: string;
}

export interface ValidateAdminAssetResponse {
  valid: boolean;
  asset?: AdminCreatedAsset;
  errors?: string[];
}

export interface CreateAdminAssetResponse {
  asset: AdminCreatedAsset;
  hydration_requested: boolean;
  hydration_request_error?: string;
}

async function parseError(response: Response, fallback: string): Promise<string> {
  const data = await response.json().catch(() => ({}));
  const details = Array.isArray(data.details) ? data.details.join(", ") : null;
  return details ? `${data.error || fallback}: ${details}` : data.error || fallback;
}

export async function getAdminAssetFormOptions(): Promise<AdminAssetFormOptions> {
  const response = await fetchWithAuth(`${API_BASE}/admin/assets/form-options`);
  if (!response.ok) {
    throw new Error(await parseError(response, "Failed to load form options"));
  }

  return response.json();
}

export async function validateAdminAsset(
  payload: AdminAssetPayload
): Promise<ValidateAdminAssetResponse> {
  const response = await fetchWithAuth(`${API_BASE}/admin/assets/validate`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return {
      valid: false,
      asset: data.asset,
      errors: Array.isArray(data.errors)
        ? data.errors
        : [data.error || "Asset validation failed"],
    };
  }

  return data;
}

export async function createAdminAsset(
  payload: AdminAssetPayload
): Promise<CreateAdminAssetResponse> {
  const response = await fetchWithAuth(`${API_BASE}/admin/assets`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await parseError(response, "Failed to create asset"));
  }

  return response.json();
}
